
import React from 'react';
import NextChargeSection from './NextChargeSection';
import BillingManager from './BillingManager';
import PaymentMethods from './PaymentMethods';
import PaymentHistory from './PaymentHistory';
import PromoCodes from './PromoCodes';
import PlanDetails from './PlanDetails';
import { 
  Business,
  Budget,
  BillingManager as BillingManagerData,
  PaymentMethod,
  DiscountCode,
  BillingHistoryItem
} from './billing/types';

interface BillingSettingsProps {
  activeBusinesses: Business[];
  pausedBusinesses: Business[];
  managedBudgets: Budget[];
  availableCredits: number;
  promotionalDiscount: number;
  totalCharge: number;
  billingManager: BillingManagerData | undefined;
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: string;
  setSelectedPaymentMethod: (id: string) => void;
  discountCodes: DiscountCode[];
  billingHistory: BillingHistoryItem[];
  handleAddPaymentMethod: () => void;
  handleAddPromoCode: () => void;
  handleMakePrimary: (methodId: string) => void;
  handleChangeBillingManager?: (userId: string) => void;
  formatCurrency: (amount: number) => string;
  isBusinessBilling?: boolean;
}

const BillingSettings = ({
  activeBusinesses = [],
  pausedBusinesses = [],
  managedBudgets = [],
  availableCredits = 0,
  promotionalDiscount = 0,
  totalCharge = 0,
  billingManager,
  paymentMethods = [],
  selectedPaymentMethod = '',
  setSelectedPaymentMethod,
  discountCodes = [],
  billingHistory = [],
  handleAddPaymentMethod,
  handleAddPromoCode,
  handleMakePrimary,
  handleChangeBillingManager,
  formatCurrency,
  isBusinessBilling = false
}: BillingSettingsProps) => {
  
  // Portfolio billing layout (original)
  if (!isBusinessBilling) {
    return (
      <div className="space-y-6 w-full overflow-hidden">
        {/* First row: PlanDetails, BillingManager */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          <div className="w-full">
            <PlanDetails />
          </div>

          <div className="w-full">
            <BillingManager 
              manager={billingManager} 
              onBillingManagerChange={handleChangeBillingManager}
            />
          </div>
        </div>

        {/* Second row: NextChargeSection, PromoCodes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          <div className="w-full">
            <NextChargeSection 
              activeBusinesses={activeBusinesses}
              pausedBusinesses={pausedBusinesses}
              managedBudgets={managedBudgets}
              availableCredits={availableCredits}
              promotionalDiscount={promotionalDiscount}
              totalCharge={totalCharge}
              formatCurrency={formatCurrency}
            />
          </div>
          
          <div className="w-full">
            <PromoCodes 
              discountCodes={discountCodes}
              handleAddPromoCode={handleAddPromoCode}
            />
          </div>
        </div>

        {/* Third row: PaymentMethods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          <div className="md:col-span-2 w-full">
            <PaymentMethods 
              paymentMethods={paymentMethods}
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              handleAddPaymentMethod={handleAddPaymentMethod}
              handleMakePrimary={handleMakePrimary}
            />
          </div>
        </div>

        {/* Fourth row: PaymentHistory */}
        <div className="w-full md:w-2/3 overflow-hidden">
          <PaymentHistory billingHistory={billingHistory} />
        </div>
      </div>
    );
  }
  
  // Business billing layout (current)
  return (
    <div className="space-y-6 w-full overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
        <div className="w-full">
          <BillingManager 
            manager={billingManager} 
            onBillingManagerChange={handleChangeBillingManager}
          />
        </div>

        <div className="w-full">
          <NextChargeSection 
            activeBusinesses={activeBusinesses}
            pausedBusinesses={pausedBusinesses}
            managedBudgets={managedBudgets}
            availableCredits={availableCredits}
            promotionalDiscount={promotionalDiscount}
            totalCharge={totalCharge}
            formatCurrency={formatCurrency}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
        <div className="md:col-span-2 w-full">
          <PaymentMethods 
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            handleAddPaymentMethod={handleAddPaymentMethod}
            handleMakePrimary={handleMakePrimary}
          />
        </div>
      </div>

      <div className="w-full md:w-2/3 overflow-hidden">
        <PaymentHistory billingHistory={billingHistory} />
      </div>
    </div>
  );
};

export default BillingSettings;
